/**
 * ============================================================================
 * LANDING PAGE / DASHBOARD - Main Dashboard and Task Assignment
 * ============================================================================
 * * This page serves two main purposes:
 * 1. Dashboard view - Display all members with search and filter
 * 2. Assign Task view - Create and assign tasks to members/teams
 * * BACKEND INTEGRATION POINTS:
 * ----------------------------------------------------------------------------
 * * 🔗 Backend: GET /api/v1/users OR /api/v1/users/all
 * Purpose: Fetch all members/users for dashboard display
 * Auth: Requires valid accessToken (admin sees all, users see team members)
 * Response: { statusCode: 200, data: [{ _id, fullname, email, team, avatar, rollNumber, ... }] }
 * * * 🔗 Backend: GET /api/v1/task/stats  <--- NEW INTEGRATION POINT
 * Purpose: Fetch Active Tasks and Completion Rate for dashboard stats
 * Auth: Requires valid accessToken (admin only)
 * Response: { statusCode: 200, data: { ActiveTask: number, completionRate: string } }
 * * * 🔗 Backend: POST /api/v1/tasks OR /api/v1/tasks/create
 * Purpose: Create new task and assign to members/teams
 * Auth: Requires valid accessToken (admin only)
 * * * 🔗 Backend: DELETE /api/v1/auth/user/:userId
 * Purpose: Delete a member/user (admin only)
 * Auth: Requires valid accessToken (admin role required)
 * URL Params: { id: string (user ID to delete) }
 * Response: { statusCode: 200, message: "User deleted successfully" }
 * ============================================================================
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchAllMembers, fetchTaskStats, createTask, deleteMember } from './services/apiService';
import Loader from './components/Loader';
import YearPromotionModal from './components/YearPromotionModal';

const LandingPage = ({ handleLogout, isDarkMode, toggleDarkMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // State for the current view (dashboard or assign-task)
  const [currentView, setCurrentView] = useState(() => {
    return location.pathname === '/assign-task' ? 'assign-task' : 'dashboard';
  });
  // State to hold the fetched and mapped members
  const [members, setMembers] = useState([]);
  // State to track member list updates (used to force re-fetch after an action)
  const [memberUpdateTrigger, setMemberUpdateTrigger] = useState(0);
  // --- NEW STATE FOR DASHBOARD STATS ---
  const [activeTasks, setActiveTasks] = useState('...'); // Initialize with loading indicator
  const [completionRate, setCompletionRate] = useState('...%'); // Initialize with loading indicator
  // --- LOADING STATE ---
  const [isLoadingMembers, setIsLoadingMembers] = useState(true); // Track loading state
  const [isSubmittingTask, setIsSubmittingTask] = useState(false); // Track task submission state
  // --- SEARCH STATE FOR TEAM MEMBERS ---
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  // --- YEAR PROMOTION MODAL STATE ---
  const [isYearModalOpen, setIsYearModalOpen] = useState(false);
  // -------------------------------------

  // --- 1. View Synchronization ---
  useEffect(() => {
    // Update currentView when location changes
    if (location.pathname === '/assign-task') {
      setCurrentView('assign-task');
    } else if (location.pathname === '/dashboard' || location.pathname === '/') {
      setCurrentView('dashboard');
    }
  }, [location.pathname]);

  // --- 2. API Data Fetching (Members - Using Cached API Service) ---
  useEffect(() => {
    const loadMembers = async () => {
      setIsLoadingMembers(true); // Start loading
      try {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          console.warn('Access token missing. Cannot fetch members.');
          setIsLoadingMembers(false);
          return;
        }

        console.log('🔄 [LandingPage] Loading members...');

        // Use cached API service
        const result = await fetchAllMembers();

        console.log('✅ [LandingPage] Members loaded:', result);

        if (result.data && result.data.transformedMembers) {
          setMembers(result.data.transformedMembers);
          console.log(`✅ [LandingPage] Set ${result.data.transformedMembers.length} members`);
        } else {
          console.warn('⚠️ [LandingPage] No members data in response');
        }
        setIsLoadingMembers(false);
      } catch (error) {
        console.error('❌ [LandingPage] Error loading members:', error);
        setIsLoadingMembers(false);
      }
    };
    loadMembers();
  }, [memberUpdateTrigger]);

  // --- 3. API Data Fetching (Task Stats - Using Cached API Service) ---
  useEffect(() => {
    const loadTaskStats = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          console.warn('Access token missing. Cannot fetch task stats.');
          setActiveTasks('N/A');
          setCompletionRate('N/A');
          return;
        }

        // Use cached API service
        const result = await fetchTaskStats();

        if (result && result.statusCode === 200) {
          const stats = result.data;
          setActiveTasks(String(stats.ActiveTask));
          const rate = parseFloat(stats.completionRate);
          const formattedRate = isNaN(rate) ? '0%' : `${Math.round(rate)}%`;
          setCompletionRate(formattedRate);
        }
      } catch (error) {
        console.error('Error loading task stats:', error);
        if (error.message.includes('401') || error.message.includes('403')) {
          setActiveTasks('Denied');
          setCompletionRate('Denied');
        } else {
          setActiveTasks('Error');
          setCompletionRate('Error');
        }
      }
    };
    loadTaskStats();
  }, [memberUpdateTrigger]);

  // Assign Task State
  const [formData, setFormData] = useState({
    taskName: '',
    description: '',
    assignedTo: [],
    priority: '',
    deadline: '',
    status: 'Not Started'
  });
  const [errors, setErrors] = useState({});
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [assignDropdownOpen, setAssignDropdownOpen] = useState(false);
  const [assignSearchTerm, setAssignSearchTerm] = useState('');
  const [priorityDropdownOpen, setPriorityDropdownOpen] = useState(false);
  const priorityConfig = {
    Low: { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
    Medium: { color: '#eab308', bg: '#fef9c3', border: '#fde047' },
    High: { color: '#ea580c', bg: '#ffedd5', border: '#fdba74' }
  };

  // Get team options - teams first, then members
  const teamOptions = [
    { id: 'management-team', name: 'Management Team', type: 'team', slug: 'management' },
    { id: 'tech-team', name: 'Tech Team', type: 'team', slug: 'tech' }, // Added slug property
    { id: 'design-team', name: 'Design Team', type: 'team', slug: 'design' },
    { id: 'pr-content-team', name: 'PR-Content Team', type: 'team', slug: 'pr-content' },
    { id: 'media-team', name: 'Media Team', type: 'team', slug: 'media' },
    { id: 'support-team', name: 'Support Team', type: 'team', slug: 'support' },
    ...members.map(m => ({ id: m.id, name: m.name, type: 'member', team: m.team, image: m.image }))
  ];

  const calculateDaysRemaining = (deadline) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadlineDate = new Date(deadline);
    deadlineDate.setHours(0, 0, 0, 0);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setDaysRemaining(diffDays);
  };

  useEffect(() => {
    if (formData.deadline) {
      calculateDaysRemaining(formData.deadline);
    }
  }, [formData.deadline]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assignDropdownOpen && !event.target.closest('[data-assign-dropdown]')) {
        setAssignDropdownOpen(false);
      }
      if (priorityDropdownOpen && !event.target.closest('[data-priority-dropdown]')) {
        setPriorityDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [assignDropdownOpen, priorityDropdownOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMultiSelect = (e) => {
    const options = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, assignedTo: options }));
    if (errors.assignedTo) {
      setErrors(prev => ({ ...prev, assignedTo: '' }));
    }
  };

  // Locate the existing toggleAssignee function (around line 170) and replace it with this:
  const toggleAssignee = (name) => {
    // 1. Find the member object using the name
    const memberOption = members.find(m => m.name === name);
    // Find the team option
    const teamOption = teamOptions.find(t => t.name === name);
    const isTeam = teamOption && teamOption.type === 'team';
    const memberExists = !!memberOption;
    // Check if the item exists at all
    if (!isTeam && !memberExists) {
      // This error check is actually more accurate now since 'members' is fetched from the API
      alert(`⚠️ Cannot assign task to "${name}". Profile not found.`);
      return;
    }
    setFormData(prev => {
      let identifier; // This will hold the ID or the Name (if it's a Team)
      if (memberOption) {
        // Use the unique ID for members
        identifier = memberOption.id;
      } else if (isTeam) {
        // Keep the name for teams (as the API doesn't use IDs for teams in the current schema)
        // The assignedTo array stores the TEAM NAME, not the slug.
        identifier = name;
      }
      if (!identifier) return prev; // Should not happen
      // Toggle the identifier (ID or Team Name) in assignedTo
      const newAssignedTo = prev.assignedTo.includes(identifier)
        ? prev.assignedTo.filter(item => item !== identifier)
        : [...prev.assignedTo, identifier];
      return { ...prev, assignedTo: newAssignedTo };
    });
    if (errors.assignedTo) {
      setErrors(prev => ({ ...prev, assignedTo: '' }));
    }
  };

  // Locate the existing removeAssignee function (around line 199) and replace it with this:
  const removeAssignee = (name) => {
    // We need to look up the ID if we are passed the name (from the display tags)
    const memberOption = members.find(m => m.name === name);
    const teamOption = teamOptions.find(t => t.name === name); // Find team by name
    // If it's a member, the assignedTo array stores the ID, otherwise it stores the Name (for teams).
    const identifierToRemove = memberOption ? memberOption.id : teamOption ? name : name;
    setFormData(prev => ({
      ...prev,
      assignedTo: prev.assignedTo.filter(item => item !== identifierToRemove)
    }));
  };

  const filteredTeamOptions = teamOptions.filter(option =>
    option.name.toLowerCase().includes(assignSearchTerm.toLowerCase())
  );

  const validateForm = () => {
    const newErrors = {};
    if (!formData.taskName.trim()) {
      newErrors.taskName = 'Task name is required';
    }
    if (formData.assignedTo.length === 0) {
      newErrors.assignedTo = 'Please assign to at least one member or team';
    }
    const invalidAssignees = formData.assignedTo.filter(idOrName => {
      // Find if it's a team name
      const isTeamName = teamOptions.some(t => t.name === idOrName && t.type === 'team');
      // Find if it's a valid member ID in the fetched list
      const isValidMemberId = members.some(m => m.id === idOrName);
      // If it's not a valid team name AND not a valid member ID, it's invalid.
      return !isTeamName && !isValidMemberId;
    });
    if (invalidAssignees.length > 0) {
      // You may want to resolve names for better error message, but using IDs is clearer for debugging:
      newErrors.assignedTo = `Cannot assign to: ${invalidAssignees.length} invalid assignee(s). Please verify profiles exist.`;
    }
    // --- END CRITICAL FIX ---
    if (formData.deadline) {
      // ... (rest of deadline validation)
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- NEW HELPER FUNCTION TO SEPARATE AND MAP ASSIGNEES ---
  const separateAssignees = (assignedToList) => {
    const memberIds = [];
    const teamSlugs = [];
    assignedToList.forEach(idOrName => {
      // 1. Check if it is a team name stored in assignedTo (e.g., 'Tech Team')
      const teamOption = teamOptions.find(t => t.name === idOrName && t.type === 'team');
      if (teamOption) {
        teamSlugs.push(teamOption.slug);
      } else {
        memberIds.push(idOrName);
      }
    });
    return { memberIds, teamSlugs };
  };
  // --- END NEW HELPER FUNCTION ---

  // 🔗 Backend: POST /api/v1/task (Used the URL from Postman image)
  // Purpose: Create new task and assign to members/teams (admin only)
  // Replaced localStorage logic with API call below
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSubmittingTask(true);
    const { memberIds, teamSlugs } = separateAssignees(formData.assignedTo);
    const assignToID = memberIds.length > 0 ? memberIds.join(',') : null;
    const assignedTeam = teamSlugs.length > 0 ? teamSlugs.join(',') : null;
    console.log('Submitting task with assignee ID:', assignToID, 'and team slug:', assignedTeam);
    const requestBody = {
      name: formData.taskName,
      description: formData.description,
      priority: (formData.priority || 'Medium').toLowerCase(),
      deadline: formData.deadline,
      assignTo: assignToID,
      assigned_team: assignedTeam
    };
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        alert('Authentication error: Access token not found. Please log in.');
        navigate('/login');
        return;
      }

      // Use API service with automatic cache invalidation
      const result = await createTask(requestBody);

      if (result.statusCode === 201) {
        // Success
        console.log('✅ Task created successfully, cache invalidated');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        // Reset form
        setFormData({
          taskName: '',
          description: '',
          assignedTo: [],
          priority: '',
          deadline: '',
          status: 'Not Started'
        });
        setDaysRemaining(null);
        // Trigger member update to refresh stats
        setMemberUpdateTrigger(prev => prev + 1);
      } else if (result.statusCode === 403) {
        alert(`Failed to create task: Permission denied (Admin access required).`);
      } else if (result.statusCode === 401) {
        window.location.reload();
      } else {
        alert(`Failed to create task: ${result.message || 'API error'}`);
      }
    } catch (error) {
      console.error('Error creating task:', error);
      alert('An error occurred while connecting to the task creation service.');
    } finally {
      setIsSubmittingTask(false);
    }
  };

  const getDeadlineMessage = () => {
    if (daysRemaining === null) return null;
    if (daysRemaining < 0) {
      return `⚠️ Overdue by ${Math.abs(daysRemaining)} days`;
    } else if (daysRemaining === 0) {
      return '✅ Due today!';
    } else if (daysRemaining === 1) {
      return '⏰ 1 day remaining';
    } else if (daysRemaining <= 3) {
      return `⏰ ${daysRemaining} days remaining`;
    } else {
      return `⏰ ${daysRemaining} days remaining`;
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  // 🔗 Backend: DELETE /api/v1/auth/user/:userId
  // Purpose: Delete a member/user from the system (admin only)
  const handleDeleteMember = async (memberId, memberName) => {
    const confirmed = window.confirm(`Are you sure you want to delete ${memberName}?`);

    if (!confirmed) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        alert('Authentication error: Access token not found. Please log in.');
        navigate('/login');
        return;
      }

      // Use API service with automatic cache invalidation
      const result = await deleteMember(memberId);

      if (result.statusCode === 200) {
        console.log('✅ Member deleted successfully, cache invalidated');
        alert(`Member "${memberName}" has been deleted successfully!`);
        setMemberUpdateTrigger(prev => prev + 1);
      } else if (result.statusCode === 403) {
        alert(`Failed to delete member: Permission denied (Admin access required).`);
      } else if (result.statusCode === 401) {
        alert(`Authentication failed: Please log in again.`);
        navigate('/login');
      } else {
        alert(`Failed to delete member: ${result.message || 'API error'}`);
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      alert('An error occurred while connecting to the member deletion service.');
    }
  };

  // --- New Helper Function (Add inside the LandingPage component) ---
  const getAssigneeName = (idOrName) => {
    // 1. Check if it's a member ID
    const member = members.find(m => m.id === idOrName);
    if (member) return member.name;
    // 2. Check if it's a team name (teams are stored by name in teamOptions)
    const team = teamOptions.find(t => t.name === idOrName && t.type === 'team');
    if (team) return team.name;
    return idOrName; // Fallback for display
  };

  const getTeamColor = (team) => {
    const colors = {
      'management': { bg: '#fff7ed', text: '#ea580c' }, // Updated team names to match data
      'tech': { bg: '#dbeafe', text: '#2563eb' },
      'design': { bg: '#fce7f3', text: '#db2777' },
      'pr-content': { bg: '#f3e8ff', text: '#7c3aed' },
      'media': { bg: '#fee2e2', text: '#dc2626' },
      'support': { bg: '#e0f2fe', text: '#0284c7' }
    };
    // Clean up team name for matching (e.g., remove ' and ' and convert to lowercase/slug)
    const normalizedTeam = team ? team.toLowerCase().replace(/ and /g, '-').replace(/\s/g, '-') : '';
    // Attempt to match the normalized team name
    for (const key in colors) {
      if (normalizedTeam.includes(key) || key.includes(normalizedTeam)) {
        return colors[key];
      }
    }
    // Fallback
    return { bg: '#f3f4f6', text: '#374151' };
  };

  // Theme variables
  const themeTextSecondary = isDarkMode ? 'rgba(255, 255, 255, 0.7)' : '#34495e';

  // Filter members based on search term
  const filteredMembers = members.filter((member) => {
    if (!memberSearchTerm.trim()) return true;
    const term = memberSearchTerm.toLowerCase().trim();
    const nameMatch = member.name?.toLowerCase().includes(term);
    const emailMatch = member.email?.toLowerCase().includes(term);
    const rollMatch = (member.roll || member.rollNumber || member.roll_no)?.toString().toLowerCase().includes(term);
    const teamMatch = member.team?.toLowerCase().includes(term);
    return nameMatch || emailMatch || rollMatch || teamMatch;
  });

  // Render the Success Toast
  return (
    <div className={`animate__animated animate__fadeIn ${isDarkMode ? 'dark-mode' : ''}`} style={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem'
    }}>
      {/* Assign Task View */}
      {currentView === 'assign-task' ? (
        <AssignTaskView
          formData={formData}
          errors={errors}
          daysRemaining={daysRemaining}
          teamOptions={teamOptions}
          priorityConfig={priorityConfig}
          handleInputChange={handleInputChange}
          handleMultiSelect={handleMultiSelect}
          handleSubmit={handleSubmit}
          getDeadlineMessage={getDeadlineMessage}
          assignDropdownOpen={assignDropdownOpen}
          isDarkMode={isDarkMode}
          setAssignDropdownOpen={setAssignDropdownOpen}
          assignSearchTerm={assignSearchTerm}
          setAssignSearchTerm={setAssignSearchTerm}
          toggleAssignee={toggleAssignee}
          removeAssignee={removeAssignee}
          filteredTeamOptions={filteredTeamOptions}
          priorityDropdownOpen={priorityDropdownOpen}
          setPriorityDropdownOpen={setPriorityDropdownOpen}
          getAssigneeName={getAssigneeName} // <-- Pass the new helper
          allMembers={members}
          isSubmittingTask={isSubmittingTask}
        />
      ) : (
        <div style={{ width: '100%' }}>
          {/* Page Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
            marginBottom: '2rem',
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.15) 100%)'
              : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(52, 152, 219, 0.08) 100%)',
            padding: window.innerWidth <= 768 ? '1.5rem' : '2rem 2.5rem',
            borderRadius: '1.5rem',
            boxShadow: isDarkMode
              ? '0 10px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              : '0 10px 40px rgba(102, 126, 234, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: isDarkMode
              ? '1px solid rgba(102, 126, 234, 0.3)'
              : '1px solid rgba(102, 126, 234, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            flexWrap: 'wrap',
            gap: window.innerWidth <= 768 ? '1.5rem' : '1rem',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            {/* Decorative Background Elements */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              right: '-10%',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: isDarkMode
                ? 'radial-gradient(circle, rgba(102, 126, 234, 0.2) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%)',
              filter: 'blur(40px)',
              pointerEvents: 'none'
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30%',
              left: '-5%',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: isDarkMode
                ? 'radial-gradient(circle, rgba(52, 152, 219, 0.2) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(52, 152, 219, 0.15) 0%, transparent 70%)',
              filter: 'blur(30px)',
              pointerEvents: 'none'
            }}></div>
            <div className="animate__animated animate__fadeInDown" style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '1rem',
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                    : 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(52, 152, 219, 0.2) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: isDarkMode
                    ? '0 8px 24px rgba(102, 126, 234, 0.3)'
                    : '0 8px 24px rgba(102, 126, 234, 0.2)',
                  border: `2px solid ${isDarkMode ? 'rgba(102, 126, 234, 0.4)' : 'rgba(102, 126, 234, 0.3)'}`
                }}>
                  <i className="fas fa-chart-pie" style={{
                    fontSize: '1.5rem',
                    color: isDarkMode ? '#818cf8' : '#667eea',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}></i>
                </div>
                <div>
                  <h2 style={{
                    fontSize: '2rem',
                    fontWeight: '800',
                    color: isDarkMode ? '#ffffff' : '#2c3e50',
                    marginBottom: '0.25rem',
                    letterSpacing: '-0.02em',
                    textShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : 'none'
                  }}>
                    Team Overview
                  </h2>
                  <p style={{
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.75)' : '#34495e',
                    fontSize: '1rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }} className="animate__animated animate__fadeIn animate__delay-1s">
                    <i className="fas fa-circle" style={{
                      fontSize: '0.375rem',
                      color: isDarkMode ? '#818cf8' : '#667eea'
                    }}></i>
                    Manage and monitor your team's activities
                  </p>
                </div>
              </div>
            </div>
            {/* Quick Actions */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              position: 'relative',
              zIndex: 1,
              flexWrap: 'wrap',
              width: window.innerWidth <= 768 ? '100%' : 'auto'
            }}>
              <button
                onClick={() => handleNavigation('/assign-task')}
                style={{
                  padding: window.innerWidth <= 480 ? '0.75rem 1rem' : '0.75rem 1.5rem',
                  background: isDarkMode
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
                  borderRadius: '0.75rem',
                  color: isDarkMode ? '#e0e7ff' : 'white',
                  fontSize: window.innerWidth <= 480 ? '0.8125rem' : '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: isDarkMode
                    ? '0 4px 12px rgba(0, 0, 0, 0.2)'
                    : '0 4px 12px rgba(102, 126, 234, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flex: window.innerWidth <= 768 ? '1 1 auto' : '0 0 auto',
                  minWidth: window.innerWidth <= 480 ? '0' : 'auto',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = isDarkMode
                    ? '0 6px 20px rgba(0, 0, 0, 0.3)'
                    : '0 6px 20px rgba(102, 126, 234, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = isDarkMode
                    ? '0 4px 12px rgba(0, 0, 0, 0.2)'
                    : '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
              >
                <i className="fas fa-plus"></i>
                New Task
              </button>
              <button
                onClick={() => handleNavigation('/create-member')}
                style={{
                  padding: window.innerWidth <= 480 ? '0.75rem 1rem' : '0.75rem 1.5rem',
                  background: isDarkMode
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.9)',
                  border: isDarkMode
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(102, 126, 234, 0.2)',
                  borderRadius: '0.75rem',
                  color: isDarkMode ? '#e0e7ff' : '#667eea',
                  fontSize: window.innerWidth <= 480 ? '0.8125rem' : '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: isDarkMode
                    ? '0 2px 8px rgba(0, 0, 0, 0.1)'
                    : '0 2px 8px rgba(0, 0, 0, 0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flex: window.innerWidth <= 768 ? '1 1 auto' : '0 0 auto',
                  minWidth: window.innerWidth <= 480 ? '0' : 'auto',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.background = isDarkMode
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'white';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.background = isDarkMode
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.boxShadow = isDarkMode
                    ? '0 2px 8px rgba(0, 0, 0, 0.1)'
                    : '0 2px 8px rgba(0, 0, 0, 0.05)';
                }}
              >
                <i className="fas fa-user-plus"></i>
                Add Member
              </button>
            </div>
          </div>
          {/* Stats Grid */}
          <div
            className="stats-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : (window.innerWidth <= 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'),
              gap: window.innerWidth <= 768 ? '1rem' : '1.5rem',
              marginBottom: window.innerWidth <= 768 ? '1rem' : '2rem',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}>
            <div
              className="card-3d"
              style={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(52, 152, 219, 0.15) 0%, rgba(52, 152, 219, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(52, 152, 219, 0.12) 0%, rgba(255, 255, 255, 0.95) 100%)',
                borderRadius: '1.25rem',
                padding: '1.75rem',
                boxShadow: isDarkMode
                  ? '0 8px 32px rgba(52, 152, 219, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                  : '0 8px 32px rgba(52, 152, 219, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                border: isDarkMode
                  ? '1px solid rgba(52, 152, 219, 0.3)'
                  : '1px solid rgba(52, 152, 219, 0.2)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                animation: 'fadeInUp 0.6s ease-out 0.2s backwards'
              }}
            >
              {/* Decorative Corner Gradient */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '120px',
                height: '120px',
                background: 'radial-gradient(circle, rgba(52, 152, 219, 0.2) 0%, transparent 70%)',
                borderRadius: '0 1.25rem 0 100%',
                pointerEvents: 'none'
              }}></div>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '1rem',
                background: 'linear-gradient(135deg, rgba(52, 152, 219, 0.2) 0%, rgba(59, 130, 246, 0.15) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1rem',
                boxShadow: '0 4px 12px rgba(52, 152, 219, 0.2)',
                border: `2px solid ${isDarkMode ? 'rgba(52, 152, 219, 0.3)' : 'rgba(52, 152, 219, 0.25)'}`,
                position: 'relative',
                zIndex: 1
              }}>
                <i className="fas fa-users" style={{
                  fontSize: '1.5rem',
                  color: isDarkMode ? '#60a5fa' : '#3498db',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}></i>
              </div>
              <h3 style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: isDarkMode ? '#60a5fa' : '#2563eb',
                marginBottom: '0.5rem',
                lineHeight: '1.2',
                position: 'relative',
                zIndex: 1,
                textShadow: isDarkMode ? '0 2px 8px rgba(96, 165, 250, 0.3)' : 'none'
              }}>
                {members.length}
              </h3>
              <p style={{
                color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : '#2c3e50',
                fontSize: '0.9375rem',
                fontWeight: '600',
                letterSpacing: '0.02em',
                position: 'relative',
                zIndex: 1
              }}>
                Total Members
              </p>
            </div>
            <div
              className="card-3d"
              style={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(236, 72, 153, 0.15) 0%, rgba(236, 72, 153, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(236, 72, 153, 0.12) 0%, rgba(255, 255, 255, 0.95) 100%)',
                borderRadius: '1.25rem',
                padding: '1.75rem',
                boxShadow: isDarkMode
                  ? '0 8px 32px rgba(236, 72, 153, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                  : '0 8px 32px rgba(236, 72, 153, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                border: isDarkMode
                  ? '1px solid rgba(236, 72, 153, 0.3)'
                  : '1px solid rgba(236, 72, 153, 0.2)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                animation: 'fadeInUp 0.6s ease-out 0.4s backwards'
              }}
            >
              {/* Decorative Corner Gradient */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '120px',
                height: '120px',
                background: 'radial-gradient(circle, rgba(236, 72, 153, 0.2) 0%, transparent 70%)',
                borderRadius: '0 1.25rem 0 100%',
                pointerEvents: 'none'
              }}></div>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '1rem',
                background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(219, 39, 119, 0.15) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1rem',
                boxShadow: '0 4px 12px rgba(236, 72, 153, 0.2)',
                border: `2px solid ${isDarkMode ? 'rgba(236, 72, 153, 0.3)' : 'rgba(236, 72, 153, 0.25)'}`,
                position: 'relative',
                zIndex: 1
              }}>
                <i className="fas fa-list-check" style={{
                  fontSize: '1.5rem',
                  color: isDarkMode ? '#f472b6' : '#ec4899',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}></i>
              </div>
              <h3 style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: isDarkMode ? '#f472b6' : '#db2777',
                marginBottom: '0.5rem',
                lineHeight: '1.2',
                position: 'relative',
                zIndex: 1,
                textShadow: isDarkMode ? '0 2px 8px rgba(244, 114, 182, 0.3)' : 'none'
              }}>
                {/* Hardcoded 12 replaced with state variable */}
                {activeTasks}
              </h3>
              <p style={{
                color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : '#2c3e50',
                fontSize: '0.9375rem',
                fontWeight: '600',
                letterSpacing: '0.02em',
                position: 'relative',
                zIndex: 1
              }}>
                Active Tasks
              </p>
            </div>
            <div
              className="card-3d"
              style={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(234, 179, 8, 0.15) 0%, rgba(234, 179, 8, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(234, 179, 8, 0.12) 0%, rgba(255, 255, 255, 0.95) 100%)',
                borderRadius: '1.25rem',
                padding: '1.75rem',
                boxShadow: isDarkMode
                  ? '0 8px 32px rgba(234, 179, 8, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                  : '0 8px 32px rgba(234, 179, 8, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                border: isDarkMode
                  ? '1px solid rgba(234, 179, 8, 0.3)'
                  : '1px solid rgba(234, 179, 8, 0.2)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                animation: 'fadeInUp 0.6s ease-out 0.6s backwards'
              }}
            >
              {/* Decorative Corner Gradient */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '120px',
                height: '120px',
                background: 'radial-gradient(circle, rgba(234, 179, 8, 0.2) 0%, transparent 70%)',
                borderRadius: '0 1.25rem 0 100%',
                pointerEvents: 'none'
              }}></div>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '1rem',
                background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.2) 0%, rgba(217, 119, 6, 0.15) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1rem',
                boxShadow: '0 4px 12px rgba(234, 179, 8, 0.2)',
                border: `2px solid ${isDarkMode ? 'rgba(234, 179, 8, 0.3)' : 'rgba(234, 179, 8, 0.25)'}`,
                position: 'relative',
                zIndex: 1
              }}>
                <i className="fas fa-chart-line" style={{
                  fontSize: '1.5rem',
                  color: isDarkMode ? '#fde047' : '#eab308',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}></i>
              </div>
              <h3 style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: isDarkMode ? '#fde047' : '#d97706',
                marginBottom: '0.5rem',
                lineHeight: '1.2',
                position: 'relative',
                zIndex: 1,
                textShadow: isDarkMode ? '0 2px 8px rgba(253, 224, 71, 0.3)' : 'none'
              }}>
                {/* Hardcoded 89% replaced with state variable */}
                {completionRate}
              </h3>
              <p style={{
                color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : '#2c3e50',
                fontSize: '0.9375rem',
                fontWeight: '600',
                letterSpacing: '0.02em',
                position: 'relative',
                zIndex: 1
              }}>
                Completion Rate
              </p>
            </div>
          </div>
          {/* Members Grid Section */}
          <div style={{
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.98) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
            borderRadius: '1.5rem',
            boxShadow: isDarkMode
              ? '0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              : '0 20px 60px rgba(31, 38, 135, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: isDarkMode
              ? '1px solid rgba(102, 126, 234, 0.2)'
              : '1px solid rgba(102, 126, 234, 0.15)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            <div style={{
              padding: '1.75rem 2.5rem',
              borderBottom: isDarkMode
                ? '2px solid rgba(102, 126, 234, 0.3)'
                : '2px solid rgba(102, 126, 234, 0.2)',
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.25) 0%, rgba(102, 126, 234, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(52, 152, 219, 0.08) 100%)',
              position: 'relative',
              overflow: 'hidden',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1.5rem',
              flexWrap: 'wrap',
            }}>
              {/* Subtle Pattern */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: isDarkMode ? 0.03 : 0.05,
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(102, 126, 234, 0.1) 10px, rgba(102, 126, 234, 0.1) 20px)',
                pointerEvents: 'none'
              }}></div>

              {/* Left: Icon + Title + Badge */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.875rem',
                position: 'relative',
                zIndex: 1,
                flexShrink: 0,
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '0.75rem',
                  background: isDarkMode
                    ? 'rgba(102, 126, 234, 0.2)'
                    : 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(52, 152, 219, 0.15) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                  border: `2px solid ${isDarkMode ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.25)'}`,
                  flexShrink: 0,
                }}>
                  <i className="fas fa-users" style={{
                    color: isDarkMode ? '#818cf8' : '#667eea',
                    fontSize: '1.25rem'
                  }}></i>
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '800',
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : '#1f2937',
                  margin: 0,
                }}>
                  Team Members
                </h3>
                <span style={{
                  fontSize: '0.8125rem',
                  fontWeight: '700',
                  color: isDarkMode ? '#818cf8' : '#667eea',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '999px',
                  background: isDarkMode
                    ? 'rgba(102, 126, 234, 0.2)'
                    : 'rgba(102, 126, 234, 0.12)',
                  border: `1.5px solid ${isDarkMode ? 'rgba(129, 140, 248, 0.4)' : 'rgba(102, 126, 234, 0.3)'}`,
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                }}>
                  {filteredMembers.length} / {members.length}
                </span>
              </div>

              {/* Right: Actions + Search Bar */}
              <div className="team-members-header-actions" style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '0.875rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="bulk-promote-trigger-btn"
                  onClick={() => setIsYearModalOpen(true)}
                  title="Batch promote academic years (e.g. 2nd Year to 3rd Year)"
                >
                  <i className="fas fa-graduation-cap"></i>
                  <span>Promote Years</span>
                </button>

                <div style={{ minWidth: '180px', flex: '1', maxWidth: '380px' }}>
                  <div className={`dashboard-search-bar ${isDarkMode ? 'dashboard-search-bar--dark' : 'dashboard-search-bar--light'}`}>
                    <i className="fas fa-search dashboard-search-icon"></i>
                    <input
                      type="text"
                      className={`dashboard-search-input ${isDarkMode ? 'dashboard-search-input--dark' : ''}`}
                      placeholder="Search members…"
                      value={memberSearchTerm}
                      onChange={(e) => setMemberSearchTerm(e.target.value)}
                      autoComplete="off"
                    />
                    {memberSearchTerm && (
                      <button
                        onClick={() => setMemberSearchTerm('')}
                        className="dashboard-search-clear"
                        title="Clear search"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Members Table Container */}
            <div
              className={`member-grid-scroll ${isDarkMode ? 'member-grid-scroll--dark' : 'member-grid-scroll--light'}`}
            >
              <div className="member-grid">
                {members.length > 0 && (
                  <div
                    className={`member-grid-row member-grid-header ${isDarkMode ? 'member-grid-header--dark' : 'member-grid-header--light'}`}
                  >
                    <div className="member-cell member-cell--name">
                      <div className="member-cell-label">
                        <i className="fas fa-user"></i>
                        <span>Member</span>
                      </div>
                    </div>
                    <div className="member-cell member-cell--team">
                      <div className="member-cell-label">
                        <i className="fas fa-users"></i>
                        <span>Team</span>
                      </div>
                    </div>
                    <div className="member-cell member-cell--email">
                      <div className="member-cell-label">
                        <i className="fas fa-envelope"></i>
                        <span>Email Address</span>
                      </div>
                    </div>
                    <div className="member-cell member-cell--roll">
                      <div className="member-cell-label">
                        <i className="fas fa-id-card"></i>
                        <span>Roll Number</span>
                      </div>
                    </div>
                    <div className="member-cell member-cell--actions">
                      <div className="member-cell-label member-cell-label--center">
                        <i className="fas fa-cog"></i>
                        <span>Actions</span>
                      </div>
                    </div>
                  </div>
                )}
                {isLoadingMembers ? (
                  <div style={{ gridColumn: '1 / -1', padding: '1rem' }}>
                    <Loader variant="page" text="Loading team members..." isDarkMode={isDarkMode} size="lg" />
                  </div>
                ) : filteredMembers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem 1.5rem', color: themeTextSecondary, width: '100%', gridColumn: '1 / -1' }}>
                    <i className="fas fa-search" style={{ fontSize: '2rem', marginBottom: '0.75rem', opacity: 0.5, display: 'block' }}></i>
                    <p style={{ margin: 0, fontSize: '1rem', fontWeight: 500 }}>No members found matching "{memberSearchTerm}"</p>
                  </div>
                ) : (
                  filteredMembers.map((member, index) => {
                    const teamColor = getTeamColor(member.team);
                    return (
                      <div
                        key={member.id}
                        className={`animate__animated animate__fadeInUp member-grid-row member-grid-body ${isDarkMode ? 'member-grid-row--dark' : 'member-grid-row--light'}`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <div className="member-cell member-cell--name" data-label="Member">
                          <div className="member-name">
                            <div className="member-avatar">
                              <img
                                src={member.image || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(member.name) + '&background=3498db&color=fff&size=128'}
                                alt={member.name}
                              />
                            </div>
                            <div className="member-name-text">
                              <span>{member.name}</span>
                            </div>
                          </div>
                        </div>
                        <div className="member-cell member-cell--team" data-label="Team">
                          <span
                            style={{
                              padding: '0.5rem 1rem',
                              borderRadius: '0.5rem',
                              fontSize: '0.8125rem',
                              fontWeight: '600',
                              backgroundColor: isDarkMode
                                ? `${teamColor.text}25`
                                : teamColor.bg,
                              color: teamColor.text,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              boxShadow: isDarkMode
                                ? `0 2px 8px ${teamColor.text}30`
                                : '0 2px 4px rgba(0, 0, 0, 0.08)',
                              border: isDarkMode
                                ? `1.5px solid ${teamColor.text}50`
                                : `1px solid ${teamColor.text}30`
                            }}
                          >
                            <i className="fas fa-circle" style={{ fontSize: '0.5rem', color: teamColor.text, opacity: 0.85 }}></i>
                            <span style={{ fontWeight: '700', letterSpacing: '0.025em' }}>{(member.team).toUpperCase()}</span>
                          </span>
                        </div>
                        <div className="member-cell member-cell--email" data-label="Email Address">
                          <div className="member-email">
                            <span>{member.email}</span>
                          </div>
                        </div>
                        <div className="member-cell member-cell--roll" data-label="Roll Number">
                          <div className="member-roll-wrapper">
                            <span className="member-roll">{member.roll}</span>
                          </div>
                        </div>
                        <div className="member-cell member-cell--actions" data-label="Actions">
                          <div className="member-actions">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/member/${member.id}`);
                              }}
                              style={{
                                padding: '0.625rem 0.875rem',
                                background: 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(52, 152, 219, 0.15) 100%)',
                                border: '1px solid rgba(52, 152, 219, 0.25)',
                                borderRadius: '0.5rem',
                                color: '#3498db',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '40px',
                                height: '40px',
                                boxShadow: '0 2px 4px rgba(52, 152, 219, 0.15)'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)';
                                e.currentTarget.style.color = 'white';
                                e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(52, 152, 219, 0.4)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(52, 152, 219, 0.15) 100%)';
                                e.currentTarget.style.color = '#3498db';
                                e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(52, 152, 219, 0.15)';
                              }}
                              title="View Details"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/edit-member/${member.id}`);
                              }}
                              style={{
                                padding: '0.625rem 0.875rem',
                                background: 'linear-gradient(135deg, rgba(46, 204, 113, 0.1) 0%, rgba(46, 204, 113, 0.15) 100%)',
                                border: '1px solid rgba(46, 204, 113, 0.25)',
                                borderRadius: '0.5rem',
                                color: '#2ecc71',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '40px',
                                height: '40px',
                                boxShadow: '0 2px 4px rgba(46, 204, 113, 0.15)'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)';
                                e.currentTarget.style.color = 'white';
                                e.currentTarget.style.transform = 'scale(1.1) rotate(-5deg)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(46, 204, 113, 0.4)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(46, 204, 113, 0.1) 0%, rgba(46, 204, 113, 0.15) 100%)';
                                e.currentTarget.style.color = '#2ecc71';
                                e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(46, 204, 113, 0.15)';
                              }}
                              title="Edit Member"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMember(member.id, member.name);
                              }}
                              style={{
                                padding: '0.625rem 0.875rem',
                                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.15) 100%)',
                                border: '1px solid rgba(239, 68, 68, 0.25)',
                                borderRadius: '0.5rem',
                                color: '#ef4444',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '40px',
                                height: '40px',
                                boxShadow: '0 2px 4px rgba(239, 68, 68, 0.15)'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                                e.currentTarget.style.color = 'white';
                                e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.15) 100%)';
                                e.currentTarget.style.color = '#ef4444';
                                e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.15)';
                              }}
                              title="Delete Member"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Academic Year Promotion Modal */}
          <YearPromotionModal
            isOpen={isYearModalOpen}
            onClose={() => setIsYearModalOpen(false)}
            members={members}
            isDarkMode={isDarkMode}
            onSuccess={() => {
              setMemberUpdateTrigger(prev => prev + 1);
            }}
          />
        </div>
      )}
      {/* Success Toast for Task Assignment (Unchanged) */}
      {showToast && (
        <div style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          background: 'linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%)',
          color: '#1f2937',
          padding: '1.25rem 2rem',
          borderRadius: '1rem',
          boxShadow: '0 10px 40px rgba(107, 114, 128, 0.4)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          zIndex: 1000,
          animation: 'slideInUp 0.3s ease-out'
        }}>
          <i className="fas fa-check-circle" style={{ fontSize: '1.5rem' }}></i>
          <span style={{ fontWeight: '700', fontSize: '1.125rem' }}>Task assigned successfully!</span>
        </div>
      )}

    </div>
  );
};

// Assign Task View Component
const AssignTaskView = ({
  formData,
  errors,
  daysRemaining,
  teamOptions,
  priorityConfig,
  handleInputChange,
  handleMultiSelect,
  handleSubmit,
  getDeadlineMessage,
  isDarkMode,
  assignDropdownOpen,
  setAssignDropdownOpen,
  assignSearchTerm,
  setAssignSearchTerm,
  toggleAssignee,
  removeAssignee,
  filteredTeamOptions,
  priorityDropdownOpen,
  setPriorityDropdownOpen,
  getAssigneeName,
  allMembers,
  isSubmittingTask
}) => {
  const isSmallScreen = window.innerWidth <= 375;
  const isDescriptionEmpty = !formData.description.trim();
  return (
    <div style={{
      width: '100%',
      maxWidth: '1600px',
      margin: '0 auto',
      padding: 0,
      animation: 'fadeInUp 0.6s ease-out',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        animation: 'fadeInDown 0.6s ease-out'
      }}>
        <h1 style={{
          fontSize: window.innerWidth <= 360 ? '1rem' : (window.innerWidth <= 480 ? '1.25rem' : (window.innerWidth <= 768 ? '1.625rem' : '2.5rem')),
          fontWeight: '800',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: window.innerWidth <= 480 ? '0.5rem' : '1rem',
          animation: 'scaleIn 0.8s ease-out',
          whiteSpace: window.innerWidth <= 768 ? 'nowrap' : 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
        }}>
          📝 Assign a New Task
        </h1>
        <p style={{
          color: '#64748b',
          fontSize: window.innerWidth <= 375 ? '0.8125rem' : (window.innerWidth <= 768 ? '1rem' : '1.125rem'),
          animation: 'fadeIn 1s ease-out 0.3s backwards',
          padding: window.innerWidth <= 375 ? '0 0.5rem' : '0'
        }}>
          Fill out the details below to assign tasks efficiently to your team.
        </p>
      </div>
      {/* Main Form - Horizontal Layout */}
      <div
        className={isDarkMode ? 'glass-card-dark' : 'glass-card'}
        style={{
          borderRadius: window.innerWidth <= 375 ? '1.25rem' : '1.5rem',
          padding: window.innerWidth <= 375 ? '1.25rem' : (window.innerWidth <= 768 ? '1.5rem' : (window.innerWidth <= 1024 ? '2rem' : '2rem 2.5rem')),
          border: isDarkMode
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(102, 126, 234, 0.2)',
          animation: 'scaleIn 0.8s ease-out 0.2s backwards',
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}
      >
        <div className="assign-task-grid" style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768
            ? '1fr'
            : window.innerWidth <= 1024
              ? '1fr 1fr'
              : '1fr 1fr 2fr',
          gap: window.innerWidth <= 768 ? '1.25rem' : '2rem',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          alignItems: 'start',
          justifyItems: 'stretch',
          justifyContent: 'stretch'
        }}>
          {/* Left Column */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.25rem',
            animation: 'fadeInLeft 0.6s ease-out 0.3s backwards'
          }}>
            {/* Task Name */}
            <div className="form-field-animate" style={{ width: '100%', boxSizing: 'border-box' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '700',
                color: '#667eea',
                marginBottom: '0.75rem',
                width: '100%'
              }}>
                Task Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                name="taskName"
                value={formData.taskName}
                onChange={handleInputChange}
                placeholder="Enter task name..."
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: `1px solid ${errors.taskName ? '#ef4444' : 'rgba(102, 126, 234, 0.2)'}`,
                  borderRadius: '0.75rem',
                  fontSize: '0.9375rem',
                  color: '#2c3e50',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)';
                  e.target.style.borderColor = '#667eea';
                }}
                onBlur={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = errors.taskName ? '#ef4444' : 'rgba(102, 126, 234, 0.2)';
                }}
              />
              {errors.taskName && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#ef4444',
                  fontSize: '0.875rem',
                  marginTop: '0.5rem',
                  animation: 'shake 0.5s ease-out'
                }}>
                  <i className="fas fa-exclamation-circle" style={{
                    animation: 'pulse 1s ease-in-out infinite'
                  }}></i>
                  {errors.taskName}
                </div>
              )}
            </div>
            {/* Description */}
            <div className="form-field-animate" style={{ width: '100%', boxSizing: 'border-box' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '700',
                color: '#667eea',
                marginBottom: '0.75rem',
                width: '100%'
              }}>
                Description / Requirements
              </label>
              <div style={{ position: 'relative', width: '100%' }}>
                {isSmallScreen && isDescriptionEmpty && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: 'rgba(100, 116, 139, 0.85)',
                      fontSize: window.innerWidth <= 320 ? '0.7rem' : '0.8125rem',
                      fontWeight: 500,
                      fontFamily: 'inherit',
                      pointerEvents: 'none',
                      textAlign: 'center',
                      width: '85%'
                    }}
                  >
                    Describe the task in detail...
                  </span>
                )}
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder={isSmallScreen ? '' : 'Describe the task in detail...'}
                  style={{
                    width: '100%',
                    padding: isSmallScreen && isDescriptionEmpty ? '1.5rem 1rem 0.875rem' : '0.875rem 1rem',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                    borderRadius: '0.75rem',
                    fontSize: '0.9375rem',
                    color: '#2c3e50',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxSizing: 'border-box',
                    textAlign: isSmallScreen && isDescriptionEmpty ? 'center' : 'left'
                  }}
                  onFocus={(e) => {
                    e.target.style.transform = 'scale(1.01)';
                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)';
                    e.target.style.borderColor = '#667eea';
                    if (isSmallScreen) {
                      e.target.style.textAlign = 'left';
                      e.target.style.padding = '0.875rem 1rem';
                    }
                  }}
                  onBlur={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = 'none';
                    e.target.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                    if (isSmallScreen) {
                      const empty = !e.target.value.trim();
                      e.target.style.textAlign = empty ? 'center' : 'left';
                      e.target.style.padding = empty ? '1.5rem 1rem 0.875rem' : '0.875rem 1rem';
                    }
                  }}
                />
              </div>
            </div>
            {/* Assign To - Beautiful Custom Select */}
            <div className="form-field-animate" style={{ position: 'relative', marginTop: '-0.5rem' }} data-assign-dropdown>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '700',
                color: '#667eea',
                marginBottom: '0.75rem'
              }}>
                <i className="fas fa-users" style={{ marginRight: '0.5rem' }}></i>
                Assign To <span style={{ color: '#ef4444' }}>*</span>
              </label>
              {/* Selected Assignees Display */}
              {formData.assignedTo.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  marginBottom: '0.75rem',
                  padding: '0.75rem',
                  background: 'rgba(102, 126, 234, 0.05)',
                  borderRadius: '0.75rem',
                  border: '1px solid rgba(102, 126, 234, 0.1)',
                  minHeight: '60px',
                  maxWidth: '100%',
                  width: '100%',
                  boxSizing: 'border-box',
                  overflow: 'hidden'
                }}>
                  {formData.assignedTo.map((idOrName) => {
                    // Resolve name for display
                    const name = getAssigneeName(idOrName);
                    // Check if it's a team (by comparing resolved name to team names in teamOptions)
                    const isTeam = teamOptions.some(t => t.name === name && t.type === 'team');
                    // Check for existence (validity)
                    const memberExists = isTeam || allMembers.some(m => m.id === idOrName);
                    return (
                      <div
                        key={idOrName} // Use ID or Name as key
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 0.75rem',
                          background: !memberExists
                            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                            : isTeam
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                          color: 'white',
                          borderRadius: '1.5rem',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          animation: 'slideIn 0.3s ease-out',
                          border: !memberExists ? '2px solid #fca5a5' : 'none',
                          maxWidth: '100%',
                          minWidth: 0,
                          flexShrink: 1
                        }}
                      >
                        <i className={`fas ${!memberExists ? 'fa-exclamation-triangle' : isTeam ? 'fa-users' : 'fa-user'}`} style={{ fontSize: '0.75rem', flexShrink: 0 }}></i>
                        <span style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: '1 1 auto'
                        }}>{name}{!memberExists ? ' (Not Found)' : ''}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Pass the resolved name to removeAssignee (it handles ID lookup internally)
                            removeAssignee(name);
                          }}
                          style={{
                            background: 'rgba(255, 255, 255, 0.3)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            minWidth: '20px',
                            minHeight: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: '0.75rem',
                            padding: 0,
                            marginLeft: '0.25rem',
                            transition: 'all 0.2s ease',
                            flexShrink: 0
                          }}
                          onMouseOver={(e) => {
                            e.target.style.background = 'rgba(255, 255, 255, 0.5)';
                            e.target.style.transform = 'scale(1.1)';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Custom Dropdown Trigger */}
              <div
                onClick={() => setAssignDropdownOpen(!assignDropdownOpen)}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: errors.assignedTo
                    ? 'rgba(255, 255, 255, 0.95)'
                    : 'rgba(255, 255, 255, 0.9)',
                  border: `2px solid ${errors.assignedTo ? '#ef4444' : assignDropdownOpen ? '#667eea' : 'rgba(102, 126, 234, 0.2)'}`,
                  borderRadius: '0.75rem',
                  fontSize: '0.9375rem',
                  color: formData.assignedTo.length === 0 ? '#94a3b8' : '#2c3e50',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s ease',
                  boxShadow: assignDropdownOpen ? '0 4px 12px rgba(102, 126, 234, 0.2)' : 'none'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className="fas fa-search" style={{ color: '#667eea' }}></i>
                  {formData.assignedTo.length === 0 ? 'Search and select teams or members...' : `${formData.assignedTo.length} selected`}
                </span>
                <i
                  className={`fas fa-chevron-${assignDropdownOpen ? 'up' : 'down'}`}
                  style={{
                    color: '#667eea',
                    transition: 'transform 0.3s ease',
                    transform: assignDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                ></i>
              </div>
              {/* Custom Dropdown Menu */}
              {assignDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '0.5rem',
                  background: 'white',
                  border: '2px solid rgba(102, 126, 234, 0.2)',
                  borderRadius: '0.75rem',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                  zIndex: 1000,
                  maxHeight: '300px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  animation: 'slideDown 0.3s ease-out',
                  transformOrigin: 'top'
                }}>
                  {/* Search Input */}
                  <div style={{
                    padding: '0.75rem',
                    borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
                    background: 'rgba(102, 126, 234, 0.02)',
                    position: 'relative'
                  }}>
                    <input
                      type="text"
                      placeholder="Search teams or members..."
                      value={assignSearchTerm}
                      onChange={(e) => setAssignSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem 0.75rem 2.5rem',
                        border: '1px solid rgba(102, 126, 234, 0.2)',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        outline: 'none',
                        background: 'white'
                      }}
                    />
                    <i className="" style={{
                      position: 'absolute',
                      left: '1.5rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#667eea',
                      fontSize: '0.875rem',
                      pointerEvents: 'none'
                    }}></i>
                  </div>
                  {/* Options List */}
                  <div style={{
                    maxHeight: '240px',
                    overflowY: 'auto',
                    padding: '0.5rem'
                  }}>
                    {filteredTeamOptions.length === 0 ? (
                      <div style={{
                        padding: '2rem',
                        textAlign: 'center',
                        color: '#94a3b8'
                      }}>
                        <i className="fas fa-search" style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.5 }}></i>
                        <p style={{ fontSize: '0.875rem' }}>No results found</p>
                      </div>
                    ) : (
                      (() => {
                        const teams = filteredTeamOptions.filter(opt => opt.type === 'team');
                        const members = filteredTeamOptions.filter(opt => opt.type === 'member');
                        return (
                          <>
                            {teams.length > 0 && (
                              <>
                                <div style={{
                                  padding: '0.5rem 0.75rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '700',
                                  color: '#667eea',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  background: 'rgba(102, 126, 234, 0.05)'
                                }}>
                                  Teams
                                </div>
                                {teams.map((option) => {
                                  // For teams, the assignedTo array stores the name
                                  const isSelected = formData.assignedTo.includes(option.name);
                                  const isTeam = option.type === 'team';
                                  return (
                                    <div key={option.id}>
                                      {/* Option Item */}
                                      <div
                                        onClick={() => toggleAssignee(option.name)}
                                        style={{
                                          padding: '0.875rem 1rem',
                                          borderRadius: '0.5rem',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '0.75rem',
                                          background: isSelected
                                            ? 'rgba(102, 126, 234, 0.1)'
                                            : 'transparent',
                                          transition: 'all 0.2s ease',
                                          marginBottom: '0.25rem'
                                        }}
                                        onMouseOver={(e) => {
                                          if (!isSelected) {
                                            e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                                          }
                                        }}
                                        onMouseOut={(e) => {
                                          if (!isSelected) {
                                            e.currentTarget.style.background = 'transparent';
                                          }
                                        }}
                                      >
                                        {/* Icon */}
                                        <div style={{
                                          width: '36px',
                                          height: '36px',
                                          borderRadius: '50%',
                                          background: isTeam
                                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                            : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: 'white',
                                          fontSize: '0.875rem',
                                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                          flexShrink: 0
                                        }}>
                                          <i className={`fas ${isTeam ? 'fa-users' : 'fa-user'}`}></i>
                                        </div>
                                        {/* Name */}
                                        <div style={{ flex: 1 }}>
                                          <div style={{
                                            fontWeight: '600',
                                            color: '#2c3e50',
                                            fontSize: '0.9375rem'
                                          }}>
                                            {option.name}
                                          </div>
                                          <div style={{
                                            fontSize: '0.75rem',
                                            color: '#94a3b8',
                                            marginTop: '0.125rem'
                                          }}>
                                            Team
                                          </div>
                                        </div>
                                        {/* Checkbox Indicator */}
                                        <div style={{
                                          width: '24px',
                                          height: '24px',
                                          borderRadius: '0.375rem',
                                          border: `2px solid ${isSelected ? '#667eea' : '#cbd5e1'}`,
                                          background: isSelected ? '#667eea' : 'white',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          transition: 'all 0.2s ease',
                                          flexShrink: 0
                                        }}>
                                          {isSelected && (
                                            <i className="fas fa-check" style={{ color: 'white', fontSize: '0.75rem' }}></i>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </>
                            )}
                            {members.length > 0 && (
                              <>
                                <div style={{
                                  padding: '0.5rem 0.75rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '700',
                                  color: '#667eea',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  background: 'rgba(102, 126, 234, 0.05)',
                                  marginTop: teams.length > 0 ? '0.5rem' : '0'
                                }}>
                                  Members
                                </div>
                                {members.map((option) => {
                                  // For members, the assignedTo array stores the ID
                                  const isSelected = formData.assignedTo.includes(option.id);
                                  return (
                                    <div key={option.id}>
                                      {/* Option Item */}
                                      <div
                                        onClick={() => toggleAssignee(option.name)}
                                        style={{
                                          padding: '0.875rem 1rem',
                                          borderRadius: '0.5rem',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '0.75rem',
                                          background: isSelected
                                            ? 'rgba(102, 126, 234, 0.1)'
                                            : 'transparent',
                                          transition: 'all 0.2s ease',
                                          marginBottom: '0.25rem'
                                        }}
                                        onMouseOver={(e) => {
                                          if (!isSelected) {
                                            e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                                          }
                                        }}
                                        onMouseOut={(e) => {
                                          if (!isSelected) {
                                            e.currentTarget.style.background = 'transparent';
                                          }
                                        }}
                                      >
                                        {/* Avatar/Image **NEW: Display Member Image** */}
                                        <img
                                          src={option.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(option.name)}&background=3498db&color=fff&size=36`}
                                          alt={option.name}
                                          style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                            flexShrink: 0
                                          }}
                                        />
                                        {/* Name */}
                                        <div style={{ flex: 1 }}>
                                          <div style={{
                                            fontWeight: '600',
                                            color: '#2c3e50',
                                            fontSize: '0.9375rem'
                                          }}>
                                            {option.name}
                                          </div>
                                          <div style={{
                                            fontSize: '0.75rem',
                                            color: '#94a3b8',
                                            marginTop: '0.125rem',
                                            // **CRITICAL CHANGE HERE**: Display the actual team name
                                          }}>
                                            {option.team}
                                          </div>
                                        </div>
                                        {/* Checkbox Indicator */}
                                        <div style={{
                                          width: '24px',
                                          height: '24px',
                                          borderRadius: '0.375rem',
                                          border: `2px solid ${isSelected ? '#667eea' : '#cbd5e1'}`,
                                          background: isSelected ? '#667eea' : 'white',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          transition: 'all 0.2s ease',
                                          flexShrink: 0
                                        }}>
                                          {isSelected && (
                                            <i className="fas fa-check" style={{ color: 'white', fontSize: '0.75rem' }}></i>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </>
                        );
                      })()
                    )}
                  </div>
                </div>
              )}
              {errors.assignedTo && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#ef4444',
                  fontSize: '0.875rem',
                  marginTop: '0.5rem'
                }}>
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.assignedTo}
                </div>
              )}
            </div>
          </div>
          {/* Right Column */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            animation: 'fadeInRight 0.6s ease-out 0.4s backwards'
          }}>
            {/* Priority */}
            <div className="form-field-animate" data-priority-dropdown style={{ position: 'relative' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '700',
                color: '#667eea',
                marginBottom: '0.75rem'
              }}>
                <i className="fas fa-bolt" style={{ fontSize: '1rem' }}></i>
                Priority
              </label>
              <div
                onClick={() => {
                  setPriorityDropdownOpen(!priorityDropdownOpen);
                  setAssignDropdownOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: formData.priority
                    ? (priorityConfig[formData.priority]?.bg || 'rgba(255, 255, 255, 0.9)')
                    : 'rgba(255, 255, 255, 0.9)',
                  border: `2px solid ${priorityDropdownOpen
                    ? '#667eea'
                    : formData.priority
                      ? (priorityConfig[formData.priority]?.border || 'rgba(102, 126, 234, 0.2)')
                      : 'rgba(102, 126, 234, 0.2)'}`,
                  borderRadius: '0.75rem',
                  fontSize: '0.9375rem',
                  color: formData.priority
                    ? (priorityConfig[formData.priority]?.color || '#2c3e50')
                    : '#94a3b8',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s ease',
                  boxShadow: priorityDropdownOpen ? '0 4px 12px rgba(102, 126, 234, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  fontWeight: formData.priority ? '600' : '400'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {formData.priority ? (
                    <>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${priorityConfig[formData.priority]?.color || '#667eea'} 0%, ${priorityConfig[formData.priority]?.color || '#764ba2'} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.875rem',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        flexShrink: 0
                      }}>
                        <i className={`fas ${formData.priority === 'High' ? 'fa-fire' :
                            formData.priority === 'Medium' ? 'fa-clock' :
                              'fa-check'
                          }`}></i>
                      </div>
                      <span>{formData.priority} Priority</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-bolt" style={{ color: '#667eea' }}></i>
                      <span>Select Priority</span>
                    </>
                  )}
                </span>
                <i
                  className={`fas fa-chevron-${priorityDropdownOpen ? 'up' : 'down'}`}
                  style={{
                    color: formData.priority
                      ? (priorityConfig[formData.priority]?.color || '#667eea')
                      : '#667eea',
                    transition: 'transform 0.3s ease',
                    fontSize: '0.875rem'
                  }}
                ></i>
              </div>
              {/* Priority Dropdown Menu */}
              {priorityDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '0.5rem',
                  background: 'white',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(102, 126, 234, 0.2)',
                  borderRadius: '0.75rem',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                  zIndex: 1000,
                  maxHeight: '300px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  animation: 'slideDown 0.3s ease-out'
                }}>
                  <div style={{
                    padding: '0.5rem',
                    maxHeight: '280px',
                    overflowY: 'auto'
                  }}>
                    {[
                      { value: 'Low', label: 'Low Priority', icon: 'fa-check-circle', emoji: '⚪', color: '#6b7280' },
                      { value: 'Medium', label: 'Medium Priority', icon: 'fa-exclamation-circle', emoji: '🟡', color: '#eab308' },
                      { value: 'High', label: 'High Priority', icon: 'fa-exclamation-triangle', emoji: '🔴', color: '#ea580c' }
                    ].map((priority) => {
                      const isSelected = formData.priority === priority.value;
                      const config = priorityConfig[priority.value];
                      return (
                        <div
                          key={priority.value}
                          onClick={() => {
                            handleInputChange({ target: { name: 'priority', value: priority.value } });
                            setPriorityDropdownOpen(false);
                          }}
                          style={{
                            padding: '0.875rem 1rem',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            background: isSelected
                              ? config.bg
                              : 'transparent',
                            transition: 'all 0.2s ease',
                            marginBottom: '0.25rem'
                          }}
                          onMouseOver={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = config.bg.replace('1)', '0.05)').replace(')', '0.05)');
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = 'transparent';
                            }
                          }}
                        >
                          <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${priority.color} 0%, ${priority.color}dd 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '0.875rem',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            flexShrink: 0
                          }}>
                            <i className={`fas ${priority.icon}`}></i>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: '600',
                              color: '#2c3e50',
                              fontSize: '0.9375rem'
                            }}>
                              {priority.emoji} {priority.label}
                            </div>
                          </div>
                          {isSelected && (
                            <i className="fas fa-check" style={{ color: priority.color, fontSize: '0.875rem' }}></i>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {formData.priority && (
                <div style={{
                  marginTop: '1rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1.25rem',
                  borderRadius: '2rem',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: priorityConfig[formData.priority].color,
                  background: priorityConfig[formData.priority].bg,
                  border: `2px solid ${priorityConfig[formData.priority].border}`,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease'
                }}>
                  <i className={`fas ${formData.priority === 'High' ? 'fa-fire' :
                      formData.priority === 'Medium' ? 'fa-clock' :
                        'fa-check'
                    }`}></i>
                  {formData.priority} Priority
                </div>
              )}
            </div>
            {/* Deadline */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '700',
                color: '#667eea',
                marginBottom: '0.75rem'
              }}>
                <i className="fas fa-calendar-alt" style={{ fontSize: '1rem' }}></i>
                Deadline
              </label>
              <input
                type="date"
                name="deadline"
                value={formData.deadline}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: `2px solid ${errors.deadline ? '#ef4444' : 'rgba(102, 126, 234, 0.2)'}`,
                  borderRadius: '0.75rem',
                  fontSize: '0.9375rem',
                  color: '#2c3e50',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.deadline ? '#ef4444' : 'rgba(102, 126, 234, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {errors.deadline && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#ef4444',
                  fontSize: '0.875rem',
                  marginTop: '0.5rem'
                }}>
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.deadline}
                </div>
              )}
              {formData.deadline && !errors.deadline && getDeadlineMessage() && (
                <div style={{
                  marginTop: '0.75rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: daysRemaining <= 3 ? '#ea580c' : '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <i className="fas fa-clock"></i>
                  {getDeadlineMessage()}
                </div>
              )}
            </div>
          </div>
          {/* Right Column - Task Preview */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            animation: 'fadeInRight 0.6s ease-out 0.5s backwards',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            alignSelf: 'start',
            justifySelf: window.innerWidth <= 768 ? 'stretch' : 'start',
            marginLeft: '0',
            paddingLeft: '0',
            gridColumn: window.innerWidth <= 768 ? '1' : window.innerWidth <= 1024 ? '1 / -1' : 'auto'
          }}>
            <div
              className={isDarkMode ? 'glass-card-dark' : 'glass-card'}
              style={{
                position: window.innerWidth <= 768 ? 'relative' : 'sticky',
                top: window.innerWidth <= 768 ? 'auto' : '2rem',
                borderRadius: window.innerWidth <= 768 ? '0.875rem' : '1rem',
                padding: window.innerWidth <= 768 ? '1.25rem' : '1.5rem',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                border: isDarkMode
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '2px solid rgba(102, 126, 234, 0.2)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                marginLeft: '0',
                marginRight: '0',
                alignSelf: 'flex-start',
                display: 'block',
                minHeight: window.innerWidth <= 768 ? 'auto' : '400px'
              }}
              onMouseMove={window.innerWidth > 768 ? (e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                const centerX = rect.width / 2;
                const centerY = rect.height / 2;
                const rotateX = (y - centerY) / 20;
                const rotateY = (centerX - x) / 20;
                e.currentTarget.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(10px)`;
              } : undefined}
              onMouseLeave={window.innerWidth > 768 ? (e) => {
                e.currentTarget.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateZ(0)';
              } : undefined}
            >
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#667eea',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                lineHeight: '1.5'
              }}>
                <i className="fas fa-eye" style={{
                  fontSize: '1.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}></i>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: '1.5'
                }}>Task Preview</span>
              </h3>
              <div style={{
                background: 'rgba(102, 126, 234, 0.05)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                border: '1px solid rgba(102, 126, 234, 0.1)'
              }}>
                {/* Task Name Preview */}
                {formData.taskName ? (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#667eea',
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      lineHeight: '1.5'
                    }}>
                      Task Name
                    </div>
                    <div style={{
                      fontSize: '1.125rem',
                      fontWeight: '700',
                      color: '#2c3e50',
                      wordBreak: 'break-word',
                      lineHeight: '1.5',
                      display: 'flex',
                      alignItems: 'flex-start'
                    }}>
                      {formData.taskName}
                    </div>
                  </div>
                ) : (
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#94a3b8',
                    fontStyle: 'italic',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: '1.5'
                  }}>
                    Enter task name to see preview...
                  </div>
                )}
                {/* Description Preview */}
                {formData.description && (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#667eea',
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      lineHeight: '1.5'
                    }}>
                      Description
                    </div>
                    <div style={{
                      fontSize: '0.9375rem',
                      color: '#64748b',
                      lineHeight: '1.6',
                      wordBreak: 'break-word',
                      display: 'flex',
                      alignItems: 'flex-start'
                    }}>
                      {formData.description}
                    </div>
                  </div>
                )}
                {/* Assignees Preview */}
                {formData.assignedTo.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#667eea',
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      lineHeight: '1.5'
                    }}>
                      Assigned To ({formData.assignedTo.length})
                    </div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      maxWidth: '100%',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}>
                      {formData.assignedTo.map((idOrName) => { // Iterate over the stored ID/Name
                        // 1. Get the name for display using the helper function
                        const name = getAssigneeName(idOrName);
                        // 2. Check if the item is an ID (member) or a Name (team)
                        const isTeam = teamOptions.some(t => t.name === name && t.type === 'team');
                        // 3. Check for existence (validity)
                        const memberExists = isTeam || allMembers.some(m => m.id === idOrName);
                        return (
                          <div
                            key={idOrName} // Use the unique ID/Name as the key
                            style={{
                              padding: '0.375rem 0.75rem',
                              background: !memberExists
                                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' // Red for Not Found/Invalid
                                : isTeam
                                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' // Blue/Purple for Team
                                  : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)', // Blue for Individual
                              color: 'white',
                              borderRadius: '0.5rem',
                              fontSize: '0.8125rem',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              border: !memberExists ? '1px solid #fca5a5' : 'none',
                              maxWidth: '100%',
                              minWidth: 0,
                              overflow: 'hidden'
                            }}
                          >
                            <i className={`fas ${!memberExists ? 'fa-exclamation-triangle' : isTeam ? 'fa-users' : 'fa-user'}`} style={{ fontSize: '0.625rem', flexShrink: 0 }}></i>
                            {/* Display the resolved name */}
                            <span style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0
                            }}>{name}{!memberExists ? ' ⚠️' : ''}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Priority Preview */}
                {formData.priority && (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#667eea',
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      lineHeight: '1.5'
                    }}>
                      Priority
                    </div>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      background: priorityConfig[formData.priority]?.bg || '#f3f4f6',
                      color: priorityConfig[formData.priority]?.color || '#374151',
                      border: `2px solid ${priorityConfig[formData.priority]?.border || '#d1d5e1'}`,
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      <i className={`fas ${formData.priority === 'High' ? 'fa-fire' :
                          formData.priority === 'Medium' ? 'fa-clock' :
                            'fa-check'
                        }`}></i>
                      {formData.priority} Priority
                    </div>
                  </div>
                )}
                {/* Deadline Preview */}
                {formData.deadline && (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#667eea',
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      lineHeight: '1.5'
                    }}>
                      Deadline
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.9375rem',
                      color: '#2c3e50',
                      fontWeight: '500'
                    }}>
                      <i className="fas fa-calendar-alt" style={{ color: '#667eea' }}></i>
                      {new Date(formData.deadline).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                    {getDeadlineMessage() && (
                      <div style={{
                        marginTop: '0.5rem',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        color: daysRemaining <= 3 ? '#ea580c' : '#6366f1',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem'
                      }}>
                        <i className="fas fa-clock"></i>
                        {getDeadlineMessage()}
                      </div>
                    )}
                  </div>
                )}
                {/* Empty State */}
                {!formData.taskName && formData.assignedTo.length === 0 && !formData.priority && !formData.deadline && (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem 1rem',
                    color: '#94a3b8'
                  }}>
                    <i className="fas fa-eye-slash" style={{
                      fontSize: '2rem',
                      marginBottom: '0.5rem',
                      opacity: 0.5,
                      display: 'block'
                    }}></i>
                    <p style={{ fontSize: '0.875rem', margin: 0 }}>
                      Fill in the form to see a live preview
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Submit Button - Full Width at Bottom */}
        <button
          onClick={handleSubmit}
          disabled={isSubmittingTask}
          className="btn-3d"
          style={{
            width: '100%',
            padding: '1.25rem 2rem',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '1rem',
            fontSize: '1.125rem',
            fontWeight: '700',
            cursor: isSubmittingTask ? 'not-allowed' : 'pointer',
            opacity: isSubmittingTask ? 0.75 : 1,
            boxShadow:
              '0 8px 25px rgba(102, 126, 234, 0.5), ' +
              '0 4px 12px rgba(118, 75, 162, 0.4), ' +
              'inset 0 2px 0 rgba(255, 255, 255, 0.2)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            marginTop: '2rem',
            position: 'relative',
            overflow: 'hidden',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem'
          }}
          onMouseOver={(e) => {
            if (!isSubmittingTask) {
              e.currentTarget.style.transform = 'translateY(-5px) translateZ(10px) scale(1.02)';
              e.currentTarget.style.boxShadow =
                '0 12px 35px rgba(102, 126, 234, 0.6), ' +
                '0 6px 18px rgba(118, 75, 162, 0.5), ' +
                'inset 0 2px 0 rgba(255, 255, 255, 0.3), ' +
                '0 0 30px rgba(102, 126, 234, 0.4)';
              e.currentTarget.style.background = 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)';
            }
          }}
          onMouseOut={(e) => {
            if (!isSubmittingTask) {
              e.currentTarget.style.transform = 'translateY(0) translateZ(0) scale(1)';
              e.currentTarget.style.boxShadow =
                '0 8px 25px rgba(102, 126, 234, 0.5), ' +
                '0 4px 12px rgba(118, 75, 162, 0.4), ' +
                'inset 0 2px 0 rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
            }
          }}
        >
          {isSubmittingTask ? (
            <Loader variant="inline" size="sm" text="Creating Task..." isDarkMode={isDarkMode} />
          ) : (
            <>
              <i className="fas fa-paper-plane" style={{
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                transition: 'transform 0.3s ease'
              }}></i>
              <span style={{
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Assign Task
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};
export default LandingPage;